// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { ReviewProvider } from './contexts/ReviewContext';
import { OrderProvider } from './contexts/OrderContext'; // ← NEW: Import OrderProvider
import StripeProvider from './components/StripeProvider';

// Hooks
import { useAuth } from './hooks/useAuth';

// Layouts
import Layout from './components/layout/Layout';
import AdminLayout from './pages/admin/AdminLayout';

// Common Components
import ScrollToTop from './components/common/ScrollToTop';

// Public Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/shop/ShopPage';
import BookDetailPage from './pages/book/BookDetailPage';
import CartPage from './pages/shop/CartPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';

// Review Page
import WriteReviewPage from './pages/review/WriteReviewPage';

// Checkout Pages
import CheckoutPage from './pages/shop/CheckoutPage';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';

// Profile Pages
import ProfilePage from './pages/Profile/ProfilePage';
import OrderHistoryPage from './pages/Profile/OrderHistoryPage';
import WishlistPage from './pages/Profile/WishlistPage';
import AccountSettingsPage from './pages/Profile/AccountSettingsPage';

// Collection Pages
import NewArrivalsPage from './pages/collection/NewArrivalsPage';
import BestSellersPage from './pages/collection/BestSellersPage';
import FeaturedPage from './pages/collection/FeaturedPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import BookList from './pages/admin/BookList';
import AddBook from './pages/admin/AddBook';
import EditBook from './pages/admin/EditBook';
import ManageNewArrivals from './pages/admin/ManageNewArrivals';
import ManageBestSellers from './pages/admin/ManageBestSellers';
import ManageFeatured from './pages/admin/ManageFeatured';
import ManageOrders from './pages/admin/ManageOrders';

// Loading
import LoadingSpinner from './components/common/LoadingSpinner';

// =======================
// Protected Routes
// =======================
const ProtectedAdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/auth/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};

const ProtectedUserRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/auth/login" replace />;
  return children;
};

// =======================
// App Component
// =======================
function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <ReviewProvider>
              <OrderProvider> {/* ← NEW: Added OrderProvider here */}
                <StripeProvider>
                  <Helmet>
                    <title>DKL Books - Nepali Novels in Toronto</title>
                    <meta
                      name="description"
                      content="Buy authentic Nepali novels online in Canada. Fast delivery across Canada."
                    />
                  </Helmet>

                  <Routes>
                    {/* Public Layout Pages */}
                    <Route element={<Layout />}>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/shop" element={<ShopPage />} />
                      <Route path="/book/:id" element={<BookDetailPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/about" element={<AboutUsPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/new-arrivals" element={<NewArrivalsPage />} />
                      <Route path="/bestsellers" element={<BestSellersPage />} />
                      <Route path="/featured" element={<FeaturedPage />} />
                      <Route path="/review/:id" element={<WriteReviewPage />} />

                      {/* Checkout */}
                      <Route
                        path="/checkout"
                        element={
                          <ProtectedUserRoute>
                            <CheckoutPage />
                          </ProtectedUserRoute>
                        }
                      />
                      <Route
                        path="/checkout/payment"
                        element={
                          <ProtectedUserRoute>
                            <PaymentPage />
                          </ProtectedUserRoute>
                        }
                      />
                      <Route path="/checkout/success" element={<PaymentSuccessPage />} />

                      {/* Profile */}
                      <Route
                        path="/profile"
                        element={
                          <ProtectedUserRoute>
                            <ProfilePage />
                          </ProtectedUserRoute>
                        }
                      />
                      <Route
                        path="/profile/orders"
                        element={
                          <ProtectedUserRoute>
                            <OrderHistoryPage />
                          </ProtectedUserRoute>
                        }
                      />
                      <Route
                        path="/profile/wishlist"
                        element={
                          <ProtectedUserRoute>
                            <WishlistPage />
                          </ProtectedUserRoute>
                        }
                      />
                      <Route
                        path="/profile/account-settings"
                        element={
                          <ProtectedUserRoute>
                            <AccountSettingsPage />
                          </ProtectedUserRoute>
                        }
                      />
                    </Route>

                    {/* Auth Pages (No Layout) */}
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/signup" element={<SignupPage />} />

                    {/* Admin Pages */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <AdminDashboard />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/books"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <BookList />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/add-book"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <AddBook />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/edit-book/:id"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <EditBook />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/manage-new-arrivals"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <ManageNewArrivals />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/manage-bestsellers"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <ManageBestSellers />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/manage-featured"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <ManageFeatured />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/orders"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout>
                            <ManageOrders />
                          </AdminLayout>
                        </ProtectedAdminRoute>
                      }
                    />

                    {/* 404 */}
                    <Route path="/not-found" element={<NotFoundPage />} />
                    <Route path="*" element={<Navigate to="/not-found" replace />} />
                  </Routes>

                  {/* Scroll to top on route change */}
                  <ScrollToTop />
                </StripeProvider>
              </OrderProvider>
            </ReviewProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;